import { FC } from "react";
import { Page } from "../templates/Page";
import styled from "styled-components";
import { DesktopSpacer } from "../templates/Spacer";
import { P1 } from "../atoms/Paragraph";
import { ComparisonTable } from "../molecules/ComparisonTable";
import { FactCardSection } from "../molecules/FactCard";
import { InlineBulletPoints } from "../molecules/BulletPoints";
import { WhyHero } from "../organisms/WhyHero";
import { OurStoryEnvironment } from "../atoms/Environments";
import { Callback } from "../organisms/Callback";
import { Column2 } from "../molecules/Column2";
import { ChatterjeeTestimonial } from "../organisms/ChatterjeeTestimonial";
import { Cloud } from "../atoms/Cloud";
import { BenefitTable } from "components/molecules/BenefitTable";
import { LatestCaseStudy } from "utils/server";

interface InspireLivesProps {
  latestCaseStudy: LatestCaseStudy;
}

const InspireLives: FC<React.PropsWithChildren<InspireLivesProps>> = ({ latestCaseStudy }) => {
  return (
    <Page
      latestCaseStudy={latestCaseStudy}
      navColor="transparent"
      hero={
        <WhyHero
          title="Your workplace has changed"
          description={
            <>
              <strong>It’s time your group life insurance caught up.</strong> So we’ve turned a
              dusty old employee perk into a life-enhancing benefit that gets people to put their
              health and wellbeing first – inside and outside work.
            </>
          }
          callToAction="See why we’re different"
          backgroundColor="#fefbe7"
          background={<OurStoryEnvironment />}
          clouds={clouds}
        />
      }
      fullScreenHero
    >
      <ComparisonTable
        comparison={[
          ["Long term protection", "Everyday protection (for the mind and body)"],
          ["Passive commodity", "Active role in driving healthy behaviour"],
          ["Minimal points of interaction", "High participation and daily engagement"],
          ["Restricted user experience", "Mobile-first consumer-friendly technology"],
          ["No personalisation", "Personalisation for everyone"],
          [
            "Insights based on a fixed moment in time",
            "Real-time employee health and wellbeing insights",
          ],
          ["Minimal additional support", "Mental health support (EAP) & Online GP"],
          ["No short-term benefits", "Exciting rewards to encourage healthy behaviour"],
          ["Low perceived value", "Tangible value and regular positive feedback"],
        ]}
      />
      <Column2
        pt="0"
        pb="0"
        title="How does a gamified experience drive employee engagement?"
        titleAlign="left"
        body={[
          "YuLife’s wellbeing app uses behavioural science and gamification to boost better physical and mental health via fantastic rewards.",
          "Whether someone wants to earn YuCoin and earn rewards, or just feel the sense of achievement that comes with completing a level, the long term gain is they live a healthier and happier life as a result.",
        ]}
      />
      <BenefitTable
        data={[
          {
            heading: "Proven engagement",
            name: "SmartPhone",
            text: "60% participate in YuLife app and 36% use the app daily.",
          },
          {
            heading: "Open to everyone",
            name: "Team",
            text: "Employees of all ages and fitness levels love and use our app for its simplicity and accessibility.",
          },
          {
            heading: "For the mind & body",
            name: "Psychology",
            text: "A balanced approach to help employees deal with the stress and anxiety that a busy life can bring.",
          },
        ]}
      />
      <ChatterjeeTestimonial pt="0" />
      <FactCardSection
        pt="0"
        pb="0"
        title="Better health at work benefits everyone"
        content="The benefits of a healthier and happier workplace are clear. YuLife can help you make a real impact at work and deliver sustainable people-first growth for your organisation."
        cards={[
          {
            top: (
              <InlineBulletPoints
                pt="0"
                textColor="#956AFF"
                points={[
                  "Better performance and growth at an individual and company level.",
                  "Reduced costs through sickness at work.",
                ]}
              />
            ),
            bottom: (
              <>
                <P1>
                  “Presenteeism (where employees work but are under-productive due to poor mental
                  wellbeing) costs the UK economy £15bn annually.”
                </P1>
                <A
                  href="https://www.centreformentalhealth.org.uk/publications/managing-presenteeism"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <P1 color="inherit" bold>
                    <Underlined>Centre for Mental Health</Underlined> 2011
                  </P1>
                </A>
              </>
            ),
          },
          {
            top: (
              <InlineBulletPoints
                pt="0"
                textColor="#956AFF"
                points={[
                  "Lower staff turnover.",
                  "More motivated, creative and productive people.",
                ]}
              />
            ),
            bottom: (
              <>
                <P1>
                  “Organisations promoting health and wellbeing are seen as 3.5 times more likely to
                  be creative and innovative.”
                </P1>
                <A
                  href="https://www.right.com/wps/wcm/connect/a2bd7426-4b2a-4af9-81ac-5211e83c72bb/the-wellness-imperative-creating-more-effective-organizations-world-economic-forum-in-partnership-with-right-management.pdf?MOD=AJPERES"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <P1 color="inherit" bold underlined>
                    The Wellness Imperative:
                  </P1>
                  <P1 pt="0" color="inherit" bold>
                    World Economic Forum 2010
                  </P1>
                </A>
              </>
            ),
          },
        ]}
      />
      <DesktopSpacer height="2.5rem" />
      <Callback />
    </Page>
  );
};

export default InspireLives;

const clouds = (
  <>
    <Cloud id="InspireLives-hero-cloud1" scale={1.8} reversed top="20%" left="5%" animated />
    <Cloud id="InspireLives-hero-cloud2" top="25%" right="10%" animated hideOnMobile />
    <Cloud
      id="InspireLives-hero-cloud3"
      scale={0.81}
      reversed
      top="30%"
      left="45%"
      animated
      hideOnMobile
    />
    <Cloud id="InspireLives-hero-cloud4" scale={1.4} bottom="45%" right="20%" animated />
    <Cloud id="InspireLives-hero-cloud5" scale={2.22} reversed bottom="15%" left="40%" animated />
  </>
);

const Underlined = styled.span`
  text-decoration: underline;
`;

const A = styled.a`
  text-decoration: none;
  color: #5c5757;

  & :hover {
    color: #e30d76;
  }
`;
